import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from "./index.module.scss";
import closeIconBlue from "../../../assets/images/closeIconBlue.svg";
import capture from "../../../assets/images/capture.webp";
import { Button } from '../Button';
import right from "../../../assets/images/right.svg";
import close from "../../../assets/images/Close.svg";
import rerecord from "../../../assets/images/re-record.png";
import { notify } from '../ToastMessage';
import { WARNING } from '../../../constants';
import { UI_TEXT, TOAST_MESSAGES, MAX_FILE_SIZE_MB, ALLOWED_FILE_TYPES } from "../../../utils/registrationUtils"

interface CameraPopUpProps {
  setShowImageCapturePopup: (show: boolean) => void;
  onFileSelect?: (file: File) => void;
}

// Utility functions
const validateFileSize = (file: File | Blob, maxSizeMB: number = MAX_FILE_SIZE_MB): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

const validateFileType = (file: File): boolean => {
  return ALLOWED_FILE_TYPES.includes(file.type.toLowerCase());
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const CameraPopUp: React.FC<CameraPopUpProps> = ({ setShowImageCapturePopup, onFileSelect }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);

  // Memoized callbacks
  const handleDivClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      if (!validateFileType(file)) {
        notify(
          TOAST_MESSAGES.INVALID_FILE_TYPE.title,
          TOAST_MESSAGES.INVALID_FILE_TYPE.message,
          WARNING
        );
        // Reset the input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Check file size
      if (!validateFileSize(file)) {
        notify(
          TOAST_MESSAGES.FILE_SIZE_TOO_LARGE.title,
          TOAST_MESSAGES.FILE_SIZE_TOO_LARGE.message(formatFileSize(file.size)),
          WARNING
        );
        // Reset the input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      if (onFileSelect) {
        onFileSelect(file);
        setShowImageCapturePopup(false);
      }
    }
  }, [onFileSelect, setShowImageCapturePopup]);

  const closeCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setCapturedFile(null);
    setIsCameraOpen(false);
  }, [stream, capturedImage]);

  const openCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      setStream(mediaStream);
      setIsCameraOpen(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      notify(
        TOAST_MESSAGES.CAMERA_ACCESS_ERROR.title,
        TOAST_MESSAGES.CAMERA_ACCESS_ERROR.message,
        WARNING
      );
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;
    
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        // Check file size
        if (!validateFileSize(blob)) {
          notify(
            TOAST_MESSAGES.IMAGE_SIZE_TOO_LARGE.title,
            TOAST_MESSAGES.IMAGE_SIZE_TOO_LARGE.message(formatFileSize(blob.size)),
            WARNING
          );
          return;
        }

        const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);
        
        setCapturedFile(file);
        setCapturedImage(imageUrl);
        
        // Stop video stream after capture
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    }, 'image/jpeg', 0.9);
  }, [stream]);

  const handleSubmit = useCallback(() => {
    if (capturedFile && onFileSelect) {
      onFileSelect(capturedFile);
      closeCamera();
      setShowImageCapturePopup(false);
    }
  }, [capturedFile, onFileSelect, closeCamera, setShowImageCapturePopup]);

  const handleDiscard = useCallback(() => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setCapturedFile(null);
    openCamera(); // Restart camera for retake
  }, [capturedImage, openCamera]);

  const handleClose = useCallback(() => {
    closeCamera();
    setShowImageCapturePopup(false);
  }, [closeCamera, setShowImageCapturePopup]);

  // Set video source when stream is available
  useEffect(() => {
    if (stream && videoRef.current && isCameraOpen) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, isCameraOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [stream, capturedImage]);

  // Extracted components for better readability
  const UploadSection = () => (
    <div className={styles.videoUploadContainer}>
      <div>
        <div onClick={handleDivClick} className={styles.upload}>{UI_TEXT.UPLOAD_PROMPT}</div>
        <div className={styles.request}>{UI_TEXT.UPLOAD_INSTRUCTION_1}</div>
        <div className={styles.request}>{UI_TEXT.UPLOAD_INSTRUCTION_2}</div>
      </div>
      <div className={styles.uploadOr}>
        <div className={styles.verticalLine}></div>
        <div>{UI_TEXT.OR_DIVIDER}</div>
        <div className={styles.verticalLine}></div>
      </div>
      <div className={styles.cameraContainer} onClick={openCamera}>
        <img src={capture} alt="camera" className={styles.captureImage} />
        <div className={styles.captureText}>{UI_TEXT.CAPTURE_PROMPT}</div>
      </div>   
    </div>
  );

  const InstructionsSection = () => (
    <div>
      <div className={styles.captureDos}>
        <div className={styles.dosDonts}>
          <img src={right} className={styles.image} alt="right"/>
        </div>
        <div className={styles.instructionLabel}>{UI_TEXT.DOS_LABEL}</div>
        <div className={styles.instruction}>
          {UI_TEXT.DOS_INSTRUCTION}
        </div>
      </div>
      <div className={styles.captureDonts}>
        <div className={styles.dosDonts}>
          <img src={close} className={styles.image} alt="close" />
        </div>
        <div className={styles.instructionLabel}>{UI_TEXT.DONTS_LABEL}</div>
        <div className={styles.instruction}>
          {UI_TEXT.DONTS_INSTRUCTION}
        </div>
      </div>
    </div>
  );

  const CameraSection = () => (
    <div className={styles.cameraView}>
      {!capturedImage ? (
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          muted
          className={styles.videoElement}
        />
      ) : (
        <img 
          src={capturedImage} 
          alt="Captured" 
          className={styles.videoElement}
        />
      )}
      
      <div className={styles.captureContainer}>
        <div>
          <img 
            src={capturedImage ? rerecord : capture} 
            alt={capturedImage ? "retake" : "capture"} 
            className={styles.imageCapture} 
            onClick={capturedImage ? handleDiscard : capturePhoto}
          />
        </div>
        <div 
          className={styles.captureText} 
          onClick={capturedImage ? handleDiscard : capturePhoto}
        >
          {capturedImage ? UI_TEXT.RETAKE_TEXT : UI_TEXT.CAPTURE_TEXT}
        </div>
      </div>

      <div className={styles.cameraControls}>
        <button onClick={closeCamera} className={styles.secondaryButton}>
          {UI_TEXT.DISCARD_BUTTON}
        </button>
        <Button 
          onClick={handleSubmit} 
          buttonClassName={styles.primaryButton}
          disable={!capturedImage}
        >
          {UI_TEXT.SUBMIT_BUTTON}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.popupOverlay}>
      <div className={`${styles.popupContent} ${isCameraOpen ? styles.cameraMode : ''}`}>
        {!isCameraOpen ? (
          <>
            <div className={styles.popupHeader}>
              <div className={styles.headerContent}>
                {UI_TEXT.HEADER_TITLE}
              </div>
              <div className={styles.lineContainer}>
                <div className={styles.horizontalLine}></div>
              </div>
              <div className={styles.closeButton} onClick={handleClose}>
                <img src={closeIconBlue} alt="closeButton" />
              </div>
            </div>
            
            <UploadSection />
            <InstructionsSection />
          </>
        ) : (
          <CameraSection />
        )}
      </div>
      
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div> 
  );
};

export default CameraPopUp;